import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Upload, FileText, Loader2, Trash2, RefreshCw, Edit2,
  ExternalLink, Search, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";

import { supabase } from "@/integrations/supabase/client";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyApplianceCategories } from "@/hooks/useCompanyApplianceCategories";

interface Manual {
  id: string;
  user_id: string;
  title: string | null;
  brand: string | null;
  models: string[];
  language: string | null;
  market: string | null;
  document_type: string | null;
  file_path: string;
  file_size: number | null;
  original_filename: string | null;
  page_count: number | null;
  metadata_status: string;
  created_at: string;
}

const DOC_TYPES = ["service_manual", "user_manual", "parts_catalog", "installation_guide", "technical_bulletin", "other"];

interface ManualLibraryProps {
  /** When true, the page is rendered inside another page (e.g. the Repair Guide Center) —
   *  hides the outer min-h-screen wrapper, page heading and back button. */
  embedded?: boolean;
}

export default function ManualLibrary({ embedded = false }: ManualLibraryProps) {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [manuals, setManuals] = useState<Manual[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<string[]>([]); // filenames currently uploading
  const [search, setSearch] = useState("");
  const [filterBrand, setFilterBrand] = useState<string>("all");
  const [filterLang, setFilterLang] = useState<string>("all");
  const [filterMarket, setFilterMarket] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const { names: companyCategoryNames } = useCompanyApplianceCategories();
  const [editing, setEditing] = useState<Manual | null>(null);
  const [deleting, setDeleting] = useState<Manual | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("repair_manuals")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      console.error(error);
      toast.error("Failed to load manuals");
    } else {
      setManuals((data ?? []) as Manual[]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleFiles = async (files: FileList | null) => {
    if (!files || !user) return;
    const arr = Array.from(files).filter(f => f.type === "application/pdf");
    if (arr.length === 0) {
      toast.error("Only PDF files are supported");
      return;
    }
    for (const file of arr) {
      setUploading(p => [...p, file.name]);
      try {
        const path = `${user.id}/${Date.now()}-${file.name.replace(/[^\w.\-]/g, "_")}`;
        const { error: upErr } = await supabase.storage
          .from("repair-guides").upload(path, file, { contentType: "application/pdf" });
        if (upErr) throw upErr;

        const companyId = await fetchCurrentCompanyId(user.id);
        if (!companyId) throw new Error("You're not assigned to a company yet — contact your admin.");
        const { data: row, error: insErr } = await supabase.from("repair_manuals").insert({
          user_id: user.id,
          company_id: companyId,
          file_path: path,
          file_size: file.size,
          original_filename: file.name,
          metadata_status: "pending",
        }).select().single();
        if (insErr) throw insErr;

        toast.success(`Uploaded ${file.name} — extracting metadata...`);
        await load();

        // Trigger AI extraction (don't await — let it run in background)
        supabase.functions.invoke("extract-manual-metadata", {
          body: { manualId: row.id, filePath: path },
        }).then(({ error }) => {
          if (error) toast.error(`Metadata extraction failed for ${file.name}`);
          else toast.success(`Metadata extracted for ${file.name}`);
          load();
        });
      } catch (e) {
        console.error(e);
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setUploading(p => p.filter(n => n !== file.name));
      }
    }
  };

  const reExtract = async (m: Manual) => {
    toast.info(`Re-extracting metadata for ${m.original_filename}...`);
    const { error } = await supabase.functions.invoke("extract-manual-metadata", {
      body: { manualId: m.id, filePath: m.file_path },
    });
    if (error) toast.error("Re-extraction failed");
    else toast.success("Metadata updated");
    load();
  };

  const openManual = async (m: Manual) => {
    const { data, error } = await supabase.storage
      .from("repair-guides").createSignedUrl(m.file_path, 300);
    if (error || !data?.signedUrl) { toast.error("Could not open manual"); return; }
    const a = document.createElement("a");
    a.href = data.signedUrl; a.target = "_blank"; a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const performDelete = async (m: Manual) => {
    const { error: stErr } = await supabase.storage.from("repair-guides").remove([m.file_path]);
    if (stErr) console.warn("storage delete:", stErr);
    const { error } = await supabase.from("repair_manuals").delete().eq("id", m.id);
    if (error) toast.error("Failed to delete manual");
    else { toast.success("Manual deleted"); load(); }
    setDeleting(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { error } = await supabase.from("repair_manuals").update({
      title: editing.title,
      brand: editing.brand,
      models: editing.models,
      language: editing.language?.toLowerCase() || null,
      market: editing.market,
      document_type: editing.document_type,
    }).eq("id", editing.id);
    if (error) toast.error("Save failed");
    else { toast.success("Saved"); setEditing(null); load(); }
  };

  const brands = useMemo(() => Array.from(new Set(manuals.map(m => m.brand).filter(Boolean))) as string[], [manuals]);
  const langs = useMemo(() => Array.from(new Set(manuals.map(m => m.language).filter(Boolean))) as string[], [manuals]);
  const markets = useMemo(() => Array.from(new Set(manuals.map(m => m.market).filter(Boolean))) as string[], [manuals]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return manuals.filter(m => {
      if (filterBrand !== "all" && m.brand !== filterBrand) return false;
      if (filterLang !== "all" && m.language !== filterLang) return false;
      if (filterMarket !== "all" && m.market !== filterMarket) return false;
      if (filterCategory !== "all") {
        // repair_manuals has no first-class category column today; match against
        // metadata_raw.category if present, otherwise leave row visible.
        const cat = (m as any).metadata_raw?.category as string | undefined;
        if (cat && cat !== filterCategory) return false;
      }
      if (!s) return true;
      return (
        m.title?.toLowerCase().includes(s) ||
        m.original_filename?.toLowerCase().includes(s) ||
        m.brand?.toLowerCase().includes(s) ||
        m.models?.some(mod => mod.toLowerCase().includes(s))
      );
    });
  }, [manuals, search, filterBrand, filterLang, filterMarket, filterCategory]);

  return (
    <div className={embedded ? "" : "min-h-screen bg-background pb-24"}>
      {!embedded && (
        <div className="px-6 pt-6 pb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-primary hover:underline mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t("common.backToData")}</span>
          </button>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("manuals.title")}</h1>
              <p className="text-muted-foreground">{t("manuals.subtitle")}</p>
            </div>
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-4 h-4 mr-2" /> Upload PDFs
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            />
          </div>
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-end mb-4">
          <Button onClick={() => fileInputRef.current?.click()}>
            <Upload className="w-4 h-4 mr-2" /> Upload PDFs
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          />
        </div>
      )}

      <div className="px-6 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, model, filename..." className="pl-9" />
        </div>
        <SearchableSelect
          className="w-[180px]"
          value={filterBrand}
          onChange={(v) => setFilterBrand(v ?? "all")}
          placeholder="Brand"
          searchPlaceholder="Search brands…"
          emptyMessage="No brands match"
          items={[
            { value: "all", label: "All brands" },
            ...brands.map((b) => ({ value: b, label: b })),
          ]}
        />

        <Select value={filterLang} onValueChange={setFilterLang}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Language" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All languages</SelectItem>
            {langs.map(l => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMarket} onValueChange={setFilterMarket}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Market" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All markets</SelectItem>
            {markets.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Appliance category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {companyCategoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {uploading.length > 0 && (
        <div className="px-6 mb-3 space-y-2">
          {uploading.map(n => (
            <div key={n} className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading {n}...
            </div>
          ))}
        </div>
      )}

      <div className="px-6">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No manuals found. Upload a PDF to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(m => (
              <div key={m.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-foreground truncate">{m.title || m.original_filename || "Untitled"}</p>
                    {m.metadata_status === "pending" && <Badge variant="secondary">Pending</Badge>}
                    {m.metadata_status === "processing" && <Badge variant="secondary"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>}
                    {m.metadata_status === "failed" && <Badge variant="destructive">Failed</Badge>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                    {m.brand && <Badge>{m.brand}</Badge>}
                    {m.document_type && <Badge variant="secondary">{m.document_type.replace(/_/g, " ")}</Badge>}
                    {m.language && <Badge variant="outline" className="uppercase">{m.language}</Badge>}
                    {m.market && <Badge variant="outline">{m.market}</Badge>}
                    {m.models?.slice(0, 5).map(mod => <Badge key={mod} variant="outline">{mod}</Badge>)}
                    {m.models && m.models.length > 5 && <Badge variant="outline">+{m.models.length - 5}</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openManual(m)} title="Open PDF">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => reExtract(m)} title="Re-run extraction">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...m })} title="Edit">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(m)} title="Delete">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit manual metadata</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={editing.title ?? ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Brand</Label>
                  <Input value={editing.brand ?? ""} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} />
                </div>
                <div>
                  <Label>Document type</Label>
                  <Select value={editing.document_type ?? ""} onValueChange={(v) => setEditing({ ...editing, document_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Language (e.g. en, de)</Label>
                  <Input value={editing.language ?? ""} onChange={(e) => setEditing({ ...editing, language: e.target.value })} />
                </div>
                <div>
                  <Label>Market (e.g. UK, DE, EU)</Label>
                  <Input value={editing.market ?? ""} onChange={(e) => setEditing({ ...editing, market: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Models (comma-separated)</Label>
                <Input
                  value={editing.models?.join(", ") ?? ""}
                  onChange={(e) => setEditing({
                    ...editing,
                    models: e.target.value.split(",").map(s => s.trim()).filter(Boolean),
                  })}
                />
                <p className="text-xs text-muted-foreground mt-1">Used to auto-suggest this manual in workflows.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this manual?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the PDF and its metadata. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleting && performDelete(deleting)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
